import React, { useState } from "react";
import "./ProjectCard.css";

// ─────────────────────────────────────────────────────────────────────────────
// ProjectCard
//
// Pure display component — receives a `project` object as a prop.
// No fetching happens here; data comes from Portfolio.js via the /projects API.
//
// Props:
//   project  – single project object from the API
//   index    – used for staggered animation delay
// ─────────────────────────────────────────────────────────────────────────────

// Icon map stays here because it's purely a UI-layer concern
// (mapping a category string to a FontAwesome class).
// If you later want this driven by the API too, add an `icon` field to
// each project object and read project.icon directly.
const CATEGORY_ICONS = {
  "Web Development":    "fa-solid fa-code",
  "Mobile Development": "fa-solid fa-mobile-screen",
  "AI Solutions":       "fa-solid fa-brain",
  "Data Visualization": "fa-solid fa-chart-bar",
  "IoT":                "fa-solid fa-microchip",
};

const ProjectCard = ({ project, index = 0 }) => {
  const [hovered, setHovered] = useState(false);

  const icon = CATEGORY_ICONS[project.category] || "fa-solid fa-folder";

  return (
    <div
      className={`pc-card ${hovered ? "pc-card--hovered" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      {/* Image */}
      <div className="pc-img-wrap">
        {project.image ? (
          <img
            src={project.image}
            alt={project.title}
            className="pc-img"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : null}

        {/* Fallback when no image */}
        <div
          className="pc-img-fallback"
          style={{ display: project.image ? "none" : "flex" }}
        >
          <i className={icon}></i>
        </div>

        {/* Hover overlay */}
        <div className="pc-overlay">
          <div className="pc-overlay-actions">
            {project.github && (
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="pc-action-btn"
                aria-label="View on GitHub"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fa-brands fa-github"></i>
                <span>Code</span>
              </a>
            )}
            {project.demo && (
              <a
                href={project.demo}
                target="_blank"
                rel="noopener noreferrer"
                className="pc-action-btn pc-action-btn--primary"
                aria-label="Live Demo"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fa-solid fa-arrow-up-right-from-square"></i>
                <span>Live Demo</span>
              </a>
            )}
          </div>
        </div>

        {/* Category badge */}
        <div className="pc-category-badge">
          <i className={icon}></i>
          {project.category}
        </div>
      </div>

      {/* Content */}
      <div className="pc-content">
        <h3 className="pc-title">{project.title}</h3>
        <p className="pc-desc">{project.description}</p>

        {/* Tech tags */}
        {project.technologies?.length > 0 && (
          <div className="pc-tags">
            {project.technologies.map((tech, i) => (
              <span key={i} className="pc-tag">
                {tech}
              </span>
            ))}
          </div>
        )}

        {/* Footer links */}
        <div className="pc-footer">
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="pc-link"
            >
              <i className="fa-brands fa-github"></i>
              Source
            </a>
          )}
          {project.demo && (
            <a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="pc-link pc-link--primary"
            >
              Live Demo
              <i className="fa-solid fa-arrow-right"></i>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
